// lib/microsite/substitutionCandidates.ts
// Plan B que el cliente puede elegir para una línea (spec sustituciones §4.2,
// entrega 1): misma categoría, se puede vender ahora, sin grupos obligatorios
// (el KDS aplicaría el cambio sin preguntar modificadores) y precio CARD crudo
// ≤ al del original. Ordenado por nombre: en el cliente no hay ventas.
//
// Súper online (revisión final PR 2b, I1): lo que se pide con selector de peso o de
// variante NUNCA es plan B. `lib/substitutions/planB.ts` no sabe de peso ni de
// variantes (escribiría precio × cantidad sin peso ni variante) y el `price <=`
// compararía $/lb con $/unidad. La señal son las claves que solo trae la fila con
// `online_retail_v2` (`weight_unit`, `variants`): sin la bandera, ninguna fila las
// trae y esto es byte a byte lo de main.
import { itemHasUnsellableRequiredGroup } from "@/lib/menu/soldOutGroups"
import { needsRetailPicker, type RetailPickerFacts } from "@/lib/microsite/retailLine"

export type CandidateItem = RetailPickerFacts & {
  id: string
  name: string
  price: number
  category_id: string | null
  is_available: boolean | null
  item_modifier_groups?: Array<{
    modifier_groups: { required: boolean | null; min_selections: number | null; modifier_options?: Array<{ is_available?: boolean | null }> } | null
  }> | null
}

export function itemHasRequiredGroup(item: CandidateItem): boolean {
  return (item.item_modifier_groups ?? []).some((g) => g.modifier_groups?.required === true || (g.modifier_groups?.min_selections ?? 0) > 0)
}

export function substitutionCandidates(original: CandidateItem, all: CandidateItem[]): CandidateItem[] {
  if (original.category_id === null) return []
  return all
    .filter((i) => i.id !== original.id)
    .filter((i) => !needsRetailPicker(i))
    .filter((i) => i.category_id === original.category_id)
    .filter((i) => i.is_available !== false)
    .filter((i) => !itemHasRequiredGroup(i))
    .filter((i) => !itemHasUnsellableRequiredGroup(i))
    .filter((i) => i.price <= original.price)
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** Lo que de una línea del carrito importa para el plan B. */
type SubstitutionLine = { itemId: string; weightMilli?: number | null; variantId?: string | null }

/**
 * ¿La línea admite elegir un producto concreto como reemplazo? Una línea por peso o
 * con variante no: solo la preferencia («Si no hay…»). Un plato siempre (main).
 */
export function lineAllowsChosenSubstitute(line: SubstitutionLine): boolean {
  return line.weightMilli == null && !line.variantId
}

/** Candidatos de plan B para una línea del carrito (vacío si es por peso o variante). */
export function cartLineSubstitutionCandidates(line: SubstitutionLine, all: CandidateItem[]): CandidateItem[] {
  if (!lineAllowsChosenSubstitute(line)) return []
  const original = all.find((m) => m.id === line.itemId)
  return original ? substitutionCandidates(original, all) : []
}
